import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useApolloClient } from '@apollo/client/react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  List,
  Modal,
  Typography,
} from 'antd';
import styled from '@emotion/styled';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';

import useAgent from 'hooks/useAgent';
import useApplicationQuery from 'hooks/useApplicationQuery';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useModalControls from 'hooks/useModalControls';
import { EFA_CONTACT_PULL, BUREAU_FRAUD_SIGNALS } from 'queries/efa';
import {
  BREAKPOINT,
  EFA_PHONE_VERIFICATION,
  SIZE,
  STATUS,
} from 'utilities/constants';

const BUREAU_VENDORS = {
  transunion: { label: 'TransUnion', vendorEnum: 'TRANSUNION' },
  experian: { label: 'Experian', vendorEnum: 'EXPERIAN' },
};

const VENDOR_KEYS = Object.keys(BUREAU_VENDORS);

const FormFieldsGrid = styled.div(() => {
  return `
    display: grid;
    grid-gap: 0 var(--spacing);
    grid-template-columns: 1fr 1fr;

    @media (min-width: ${BREAKPOINT.LG_PX}) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  `;
});

const EFAContactPullPage = () => {
  const agent = useAgent();
  const { applicationId } = useParams();
  const { customerInfo, customerInfoQuery } = useCustomerInfo();
  const confirmationModal = useModalControls();
  const [form] = Form.useForm();
  const apolloClient = useApolloClient();

  const [errorVendors, setErrorVendors] = useState({});
  const [loadingVendors, setLoadingVendors] = useState({});
  const [results, setResults] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(undefined);
  const autoLoadTriggered = useRef(false);

  const { application, applicationQuery, bureauReports } =
    useApplicationQuery(applicationId);

  const requiresVerification = useMemo(() => {
    return application && application.statusReason === EFA_PHONE_VERIFICATION;
  }, [application]);

  const latestDocByVendor = useMemo(() => {
    const byVendor = {};
    for (const report of bureauReports) {
      if (!report?.requestId || !report?.vendor) {
        continue;
      }
      const vendor = report.vendor.toLowerCase();
      if (
        !byVendor[vendor] ||
        new Date(report.fetchedAt) > new Date(byVendor[vendor].fetchedAt)
      ) {
        byVendor[vendor] = report;
      }
    }
    return byVendor;
  }, [bureauReports]);

  useEffect(() => {
    if (!customerInfoQuery.loading && customerInfo) {
      const { firstName, lastName, dateOfBirth } = customerInfo;
      const { city, state, street1, postalCode } = customerInfo.address || {};

      form.setFieldsValue({
        firstName,
        lastName,
        dateOfBirth,
        street1,
        city,
        state,
        zip: postalCode,
      });
    }
  }, [customerInfo, customerInfoQuery]);

  useEffect(() => {
    if (
      autoLoadTriggered.current ||
      !requiresVerification ||
      !agent.loaded ||
      !agent.isSuperUser ||
      applicationQuery.loading ||
      !application
    ) {
      return;
    }

    autoLoadTriggered.current = true;
    VENDOR_KEYS.forEach((vendor) => loadExistingStatements(vendor));
  }, [
    requiresVerification,
    agent.loaded,
    agent.isSuperUser,
    application,
    applicationQuery.loading,
    latestDocByVendor,
  ]);

  const showConfirmation = (vendor) => {
    setSelectedVendor(vendor);
    confirmationModal.show();
  };

  const onExecutePullClick = async (vendor) => {
    confirmationModal.hide();
    handleSubmit(vendor);
  };

  const loadExistingStatements = async (vendor) => {
    const report = latestDocByVendor[vendor];
    if (!report) {
      return;
    }

    setErrorVendors((prev) => ({ ...prev, [vendor]: false }));
    setLoadingVendors((prev) => ({ ...prev, [vendor]: true }));

    try {
      // reads consumer statements already recorded in Chalk, without hitting the bureau.
      const response = await apolloClient.query({
        query: BUREAU_FRAUD_SIGNALS,
        variables: {
          documentId: report.requestId,
          vendor: BUREAU_VENDORS[vendor].vendorEnum,
        },
        fetchPolicy: 'network-only',
      });
      setResults((prev) => ({
        ...prev,
        [vendor]: response.data.bureauFraudSignals?.consumerStatements ?? [],
      }));
    } catch {
      setErrorVendors((prev) => ({ ...prev, [vendor]: true }));
    } finally {
      setLoadingVendors((prev) => ({ ...prev, [vendor]: false }));
    }
  };

  let handleSubmit = async (vendor) => {
    setErrorVendors((prev) => ({ ...prev, [vendor]: false }));
    setLoadingVendors((prev) => ({ ...prev, [vendor]: true }));

    try {
      const response = await apolloClient.query({
        query: EFA_CONTACT_PULL,
        variables: {
          applicationId: application?.applicationId || '',
          vendor: BUREAU_VENDORS[vendor].vendorEnum,
        },
        fetchPolicy: 'network-only',
      });
      setResults((prev) => ({
        ...prev,
        [vendor]: response.data.pullEfaStatements?.statements ?? [],
      }));
    } catch {
      setErrorVendors((prev) => ({ ...prev, [vendor]: true }));
    } finally {
      setLoadingVendors((prev) => ({ ...prev, [vendor]: false }));
    }
  };

  return (
    <>
      <Modal
        {...confirmationModal}
        id="efaPullConfirmationModal"
        title={`
          You are about to perform a soft
          ${BUREAU_VENDORS[selectedVendor]?.label ?? ''}
          credit re-pull
        `}
        okText="Execute Soft Re-pull"
        onOk={() => onExecutePullClick(selectedVendor)}
        okButtonProps={{
          'data-testid': 'submit-button',
        }}
      >
        <Flex
          data-testid="confirmation-modal"
          vertical={true}
          gap="var(--spacing-sm)"
        >
          <Typography.Text>
            You are about to execute a soft{' '}
            {BUREAU_VENDORS[selectedVendor]?.label} re-pull for this customer.
            This request may take a while to be processed. Are you sure you want
            to continue?
          </Typography.Text>
        </Flex>
      </Modal>
      <Card title="EFA Contact Pull">
        <Form form={form} initialValues={{}} layout="vertical">
          <QueryBoundary
            mode={QueryBoundary.MODE.MESSAGE}
            loadingMessage="Loading Application and Applicant Information"
            loadingOverride={
              customerInfoQuery.loading ||
              applicationQuery.loading ||
              !agent.loaded
            }
            query={applicationQuery}
          >
            {agent.isSuperUser ? (
              <>
                <FormFieldsGrid>
                  <Form.Item label="First Name" name="firstName">
                    <Input readOnly />
                  </Form.Item>
                  <Form.Item label="Last Name" name="lastName">
                    <Input readOnly />
                  </Form.Item>
                  <Form.Item label="Date of Birth" name="dateOfBirth">
                    <Input data-testid="date-of-birth-input" readOnly />
                  </Form.Item>
                  <Form.Item
                    label="Street"
                    name="street1"
                    style={{
                      gridColumn: '1/3',
                    }}
                  >
                    <Input readOnly />
                  </Form.Item>
                  <Form.Item
                    label="City"
                    name="city"
                    style={{
                      gridColumn: '1',
                    }}
                  >
                    <Input readOnly />
                  </Form.Item>
                  <Form.Item label="State" name="state">
                    <Input readOnly />
                  </Form.Item>
                  <Form.Item label="Zip Code" name="zip">
                    <Input readOnly />
                  </Form.Item>
                </FormFieldsGrid>

                {!requiresVerification ? (
                  <Alert
                    banner
                    type="warning"
                    style={{
                      maxWidth: 'fit-content',
                      marginBottom: 'var(--spacing)',
                    }}
                    title="This application is not in need of identify verification."
                  />
                ) : null}

                {VENDOR_KEYS.filter((vendor) => errorVendors[vendor]).map(
                  (vendor) => (
                    <Alert
                      key={vendor}
                      data-testid={`${vendor}-error-message`}
                      type="error"
                      style={{
                        maxWidth: 'fit-content',
                        marginBottom: 'var(--spacing)',
                      }}
                      title={`Failed to retrieve ${BUREAU_VENDORS[vendor].label} EFA contact information. Please try again.`}
                    />
                  ),
                )}

                <Flex gap="var(--spacing-sm)">
                  <Button
                    data-testid="execute-transunion-pull-button"
                    type="primary"
                    disabled={!requiresVerification}
                    loading={loadingVendors.transunion}
                    onClick={() => showConfirmation('transunion')}
                  >
                    Re-pull TransUnion
                  </Button>
                  <Button
                    data-testid="execute-experian-pull-button"
                    type="primary"
                    disabled={!requiresVerification}
                    loading={loadingVendors.experian}
                    onClick={() => showConfirmation('experian')}
                  >
                    Re-pull Experian
                  </Button>
                </Flex>
              </>
            ) : (
              // TODO: determine if this is still necessary
              <SectionMessage
                data-testid="no-efa-access-message"
                status={STATUS.ERROR}
                size={SIZE.SM}
                text="Sorry, you lack the permissions to execute an EFA Contact Pull"
                visible={true}
              ></SectionMessage>
            )}
          </QueryBoundary>
        </Form>

        {Object.keys(results).length > 0 && (
          <>
            <Divider></Divider>
            <Flex vertical={true} gap="var(--spacing)">
              {VENDOR_KEYS.filter((vendor) => results[vendor]).map((vendor) => {
                const statements = results[vendor];
                return (
                  <Card
                    key={vendor}
                    type="inner"
                    title={BUREAU_VENDORS[vendor].label}
                    data-testid={`${vendor}-result-card`}
                  >
                    {statements?.length ? (
                      <List
                        data-testid={`${vendor}-statement-list`}
                        size="small"
                        bordered
                        dataSource={statements}
                        renderItem={(item) => (
                          <List.Item>
                            <Typography.Text code={true}>
                              {item}
                            </Typography.Text>
                          </List.Item>
                        )}
                      />
                    ) : (
                      <SectionMessage
                        data-testid={`${vendor}-no-statement-message`}
                        status={STATUS.INFORMATION}
                        size={SIZE.SM}
                        text="No consumer statement found in the credit report"
                        visible={true}
                      />
                    )}
                  </Card>
                );
              })}
            </Flex>
          </>
        )}
      </Card>
    </>
  );
};

export default EFAContactPullPage;
